import type { DrinkId } from './bar';
import type { GameId } from './games/types';

/**
 * The Gift Shop: permanent upgrades bought with in-run cash, open any time
 * from the hub, taking effect from the NEXT run. What's on the shelves
 * depends on the highest floor you've ever reached. There's no separate
 * currency and no end-of-run shopping screen — spending here is a real
 * trade against the current run.
 *
 * Balance note (see CLAUDE.md): the climb simulation found the no-drinks
 * first climb nearly hopeless, with most runs dying on the Main Floor.
 * Starting Cash is the main lever for easing later runs; nothing here makes
 * a game itself positive-EV.
 */

export type UpgradeId =
  | 'cash1'
  | 'cash2'
  | 'cash3'
  | 'cash4'
  | 'tolerance1'
  | 'tolerance2'
  | 'metabolism'
  | 'elevator'
  | 'welcome'
  | 'slot'
  | 'happyhour'
  | 'sugar1'
  | 'metabolism2'
  | 'sugar2'
  | 'elevator2'
  | 'cash5'
  | 'tolerance3'
  | 'vip'
  /** Buy the Casino — never owned, it ends the run (see CASINO_PRICE). */
  | 'casino'
  | `install:${GameId}`
  | `recipe:${DrinkId}`;

/** Price of Buy the Casino (Penthouse Gift Shop only). It isn't an upgrade:
 *  buying it wins the run. ~$150k, scaled with the 2026-09-22 fare cut. */
export const CASINO_PRICE = 150_000;

export interface UpgradeDef {
  id: UpgradeId;
  name: string;
  effect: string;
  price: number;
  /** Highest-floor-ever needed before it shows up. */
  unlockFloor: number;
  /** Earlier tier that has to be owned first. */
  requires?: UpgradeId;
}

const STARTING_CASH: Record<string, number> = { cash1: 50, cash2: 100, cash3: 150, cash4: 250, cash5: 500 };
/** VIP Pass: once a run, the house covers a bust with a marker worth this
 *  many typical bets on your floor (never paid back). User, 2026-09-23:
 *  replaced "start on the Mezzanine with $1,000 extra". */
export const MARKER_BETS = 5;

const install = (id: GameId, name: string, floor: number, price: number): UpgradeDef => ({
  id: `install:${id}`,
  name: `Install ${name}`,
  // Every floor carries the floors below it, so an installed game is on
  // every floor (user, 2026-09-27: say so plainly).
  effect: `Play ${name} on every floor, starting from the Main Floor, in every run.`,
  price,
  unlockFloor: floor,
});

/** `short` names the drink in the shop list (the lens's list pane is ~25
 *  characters); `name` is the full drink name used in the description. */
const recipe = (id: DrinkId, short: string, name: string, floor: number, price: number): UpgradeDef => ({
  id: `recipe:${id}`,
  name: `Recipe: ${short}`,
  effect: `Every bar pours the ${name}, Main Floor included.`,
  price,
  unlockFloor: floor,
});

export const UPGRADES: UpgradeDef[] = [
  { id: 'cash1', name: 'Starting Cash I', effect: 'Start every run with $50 more.', price: 200, unlockFloor: 1 },
  { id: 'cash2', name: 'Starting Cash II', effect: 'Start every run with another $100.', price: 600, unlockFloor: 1, requires: 'cash1' },
  { id: 'cash3', name: 'Starting Cash III', effect: 'Start every run with another $150.', price: 1500, unlockFloor: 1, requires: 'cash2' },
  { id: 'cash4', name: 'Starting Cash IV', effect: 'Start every run with another $250.', price: 4000, unlockFloor: 2, requires: 'cash3' },
  { id: 'welcome', name: 'Welcome Drink', effect: 'Every run starts with an Espresso Martini on the house.', price: 800, unlockFloor: 2 },
  // Liquid Courage I-III (user, 2026-09-23) replace Tolerance I-III, which
  // only moved the blackout line — a line nobody sensible gets near. Paced by
  // strength (user): I where Tipsy first becomes reachable, II makes it easy,
  // III (every table) last. The ids stay tolerance1-3 so old saves keep what
  // they bought.
  { id: 'tolerance1', name: 'Liquid Courage I', effect: 'While Tipsy, your ★ table is even luckier: more second chances.', price: 800, unlockFloor: 2 },
  install('crash', 'Crash', 2, 500),
  install('liarsdice', "Liar's Dice", 2, 500),
  install('baccarat', 'Baccarat', 2, 500),
  install('highlow', 'High-Low', 2, 500),
  recipe('espresso', 'Esp. Martini', 'Espresso Martini', 2, 400),
  { id: 'tolerance2', name: 'Liquid Courage II', effect: 'You count as Tipsy from Buzz 3 instead of 4.', price: 3000, unlockFloor: 3, requires: 'tolerance1' },
  // Booger Sugar (user, 2026-09-23): drinks come back in stock faster.
  { id: 'sugar1', name: 'Booger Sugar I', effect: 'Drinks restock faster: in 1.5x their duration, not 1.75x.', price: 2000, unlockFloor: 3 },
  // Tolerance (user, 2026-09-25) replaces Fast Metabolism (id kept for old
  // saves): it widens Tipsy upward instead of speeding the fade, and the
  // blackout line stays at 10.
  { id: 'metabolism', name: 'Tolerance I', effect: 'You stay Tipsy up to Buzz 7: Drunk starts at 8, not 7. Blackout is still 10.', price: 1200, unlockFloor: 3 },
  { id: 'elevator', name: 'Elevator Discount', effect: 'Every elevator fare is 15% cheaper.', price: 1000, unlockFloor: 3 },
  install('mines', 'Mines', 3, 1500),
  install('roulette', 'Roulette', 3, 1500),
  install('craps', 'Craps', 3, 1500),
  install('threecard', '3-Card Poker', 3, 1500),
  install('holdem', "Hold'em", 3, 1500),
  recipe('happy', 'Happy Drunk', 'Happy Drunk', 3, 1200),
  recipe('lastcall', 'Last Call', 'Last Call', 4, 3000),
  // Installs for the top two floors (user, 2026-09-27: the shop was missing
  // them; only the Mezzanine and Salon games could be installed).
  install('bigsix', 'Big Six', 4, 5000),
  install('war', 'Casino War', 4, 5000),
  install('dice', 'Dice', 4, 5000),
  install('videopoker', 'Video Poker', 4, 5000),
  { id: 'slot', name: 'Drink Slot +1', effect: 'Four drinks going at once instead of three.', price: 8000, unlockFloor: 4 },
  { id: 'happyhour', name: 'Happy Hour', effect: 'Every drink costs 25% less.', price: 6000, unlockFloor: 4 },
  { id: 'metabolism2', name: 'Tolerance II', effect: 'You stay Tipsy up to Buzz 8: Drunk starts at 9. Blackout is still 10.', price: 5000, unlockFloor: 4, requires: 'metabolism' },
  { id: 'tolerance3', name: 'Liquid Courage III', effect: 'While Tipsy, every table plays with ★ luck.', price: 8000, unlockFloor: 4, requires: 'tolerance2' },
  { id: 'elevator2', name: 'Elevator Discount II', effect: 'Elevator fares 30% cheaper in total.', price: 10000, unlockFloor: 4, requires: 'elevator' },
  { id: 'cash5', name: 'Starting Cash V', effect: 'Start every run with another $500.', price: 15000, unlockFloor: 5, requires: 'cash4' },
  { id: 'sugar2', name: 'Booger Sugar II', effect: 'Drinks restock even faster: in 1.25x their duration.', price: 12000, unlockFloor: 5, requires: 'sugar1' },
  install('tower', 'Tower', 5, 15000),
  install('plinko', 'Plinko', 5, 15000),
  install('horses', 'Horse Racing', 5, 15000),
  install('mahjong', 'Mahjong', 5, 15000),
  { id: 'vip', name: 'VIP Pass', effect: `Once a run, if you go broke, the house fronts you ${MARKER_BETS} bets.`, price: 40000, unlockFloor: 5 },
];

export function upgradeDef(id: UpgradeId): UpgradeDef | undefined {
  return UPGRADES.find((u) => u.id === id);
}

/** What's on the shelves: unlocked, not owned, earlier tier owned. */
export function shopItems(owned: readonly UpgradeId[], highestFloor: number): UpgradeDef[] {
  return UPGRADES.filter((u) => u.unlockFloor <= highestFloor && !owned.includes(u.id) && (!u.requires || owned.includes(u.requires)));
}

/** Items that exist but need a higher floor first (for a teaser line). */
export function lockedCount(owned: readonly UpgradeId[], highestFloor: number): number {
  return UPGRADES.filter((u) => u.unlockFloor > highestFloor && !owned.includes(u.id)).length;
}

/**
 * Everything the upgrades change about a run, fixed when the run starts —
 * so buying something mid-run never changes the run you're in.
 */
export interface RunPerks {
  startingBonus: number;
  maxBuzz: number;
  /** Liquid Courage tiers owned (0-3). */
  courage: number;
  soberEvery: number;
  /** Buzz where Drunk starts (Tolerance: 8). */
  drunkAt: number;
  /** Fraction taken off every elevator fare. */
  elevatorDiscount: number;
  installed: GameId[];
  recipes: DrinkId[];
  welcomeDrink: boolean;
  drinkSlots: number;
  /** Multiplier on drink prices (Happy Hour: 0.75). */
  drinkPriceFactor: number;
  /** Taken off the drink restock factor (Booger Sugar). */
  restockCut: number;
  /** VIP Pass: the house covers one bust a run with a marker. */
  marker: boolean;
}

export const NO_PERKS: RunPerks = {
  startingBonus: 0,
  maxBuzz: 10,
  courage: 0,
  soberEvery: 5,
  drunkAt: 7,
  elevatorDiscount: 0,
  installed: [],
  recipes: [],
  welcomeDrink: false,
  drinkSlots: 3,
  drinkPriceFactor: 1,
  restockCut: 0,
  marker: false,
};

export function perksFor(owned: readonly UpgradeId[]): RunPerks {
  const has = (id: UpgradeId) => owned.includes(id);
  return {
    startingBonus: owned.reduce((sum, id) => sum + (STARTING_CASH[id] ?? 0), 0),
    maxBuzz: 10,
    courage: (has('tolerance1') ? 1 : 0) + (has('tolerance2') ? 1 : 0) + (has('tolerance3') ? 1 : 0),
    soberEvery: 5,
    drunkAt: has('metabolism2') ? 9 : has('metabolism') ? 8 : 7,
    elevatorDiscount: has('elevator2') ? 0.3 : has('elevator') ? 0.15 : 0,
    installed: owned.filter((id) => id.startsWith('install:')).map((id) => id.slice('install:'.length) as GameId),
    recipes: owned.filter((id) => id.startsWith('recipe:')).map((id) => id.slice('recipe:'.length) as DrinkId),
    welcomeDrink: has('welcome'),
    drinkSlots: has('slot') ? 4 : 3,
    drinkPriceFactor: has('happyhour') ? 0.75 : 1,
    restockCut: (has('sugar1') ? 0.25 : 0) + (has('sugar2') ? 0.25 : 0),
    marker: has('vip'),
  };
}
